'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import Sidebar from './Sidebar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GenerationHistory } from './GenerationHistory';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogClose,
  DialogPortal,
  DialogOverlay,
} from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Home, History, Settings, Menu, PanelLeft, Clock, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile'; // 引入 useIsMobile 钩子
import { SidebarProvider } from '@/contexts/SidebarContext'; // 导入 SidebarProvider
import { usePathname } from 'next/navigation';
import Header from '@/components/anyvideo/HeaderOptimized';

// 创建自定义对话框内容组件，不包含默认的关闭按钮
const CustomDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className="bg-black/60 backdrop-blur-sm transition-all duration-300" />
    <DialogPrimitive.Content ref={ref} className={cn('', className)} {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
CustomDialogContent.displayName = 'CustomDialogContent';

interface GeneratorLayoutProps {
  children: React.ReactNode;
  hasBaseScrollContentWrap?: boolean;
  innerScroll?: boolean;
}

export function GeneratorLayout({
  children,
  hasBaseScrollContentWrap = true,
  innerScroll = true,
}: GeneratorLayoutProps) {
  const [showHistory, setShowHistory] = React.useState(false);
  const [showSidebar, setShowSidebar] = React.useState(false);
  const isMobile = useIsMobile(); // 使用 useIsMobile 钩子
  const [localeModalVisible, setLocaleModalVisible] = React.useState(false);

  return (
    <SidebarProvider>
      <>
        {/* PC端布局 */}
        {!isMobile && (
          <div className={`h-screen overflow-hidden ${localeModalVisible ? 'mt-[48px]' : ''}`}>
            <div className="flex h-screen bg-gradient-to-b from-black via-black/95 to-black text-white">
              {/* 侧边栏 */}
              <motion.div
                className="w-[280px] flex-shrink-0 h-full relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Sidebar />
                {/* 电影感右边框 */}
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-emerald-400/60 to-transparent" />
                <div className="absolute top-0 right-0 w-0.5 h-full bg-gradient-to-b from-emerald-500/20 via-emerald-400/40 to-emerald-500/20 blur-sm" />
              </motion.div>

              {/* 主要内容区域 */}
              <motion.div
                className="flex-1 h-full relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                style={{
                  background: `linear-gradient(135deg, 
                    rgba(0,0,0,0.95) 0%, 
                    rgba(6,20,14,0.90) 30%, 
                    rgba(0,20,15,0.85) 70%, 
                    rgba(0,0,0,0.95) 100%)`,
                }}
              >
                {/* 电影感背景装饰 */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {/* 网格背景 */}
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50" />
                  
                  {/* 光点装饰 */}
                  <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-emerald-400/30 rounded-full animate-pulse delay-300" />
                  <div className="absolute top-3/4 right-1/3 w-1.5 h-1.5 bg-green-300/20 rounded-full animate-ping delay-700" />
                  <div className="absolute top-1/2 right-1/6 w-3 h-3 bg-emerald-500/20 rounded-full animate-bounce delay-1000" />
                  
                  {/* 渐变边缘 */}
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
                  
                  {/* 侧边渐变阴影 */}
                  <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-emerald-500/5 to-transparent" />
                  <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-emerald-500/5 to-transparent" />
                </div>
                
                {/* 内容区域 */}
                <div className="relative z-10 h-full">
                  {innerScroll ? (
                    <ScrollArea className="h-full">
                      {hasBaseScrollContentWrap ? (
                        <div className="h-full p-4 md:p-6">{children}</div>
                      ) : (
                        children
                      )}
                    </ScrollArea>
                  ) : (
                    children
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* 移动端布局 */}
        {isMobile && (
          <div
            className={`min-h-screen bg-gradient-to-b from-black via-black/95 to-black text-white xl:hidden`}
          >
            <Header />
            <main className="min-h-[calc(100vh-4rem)] pt-16">
              <div className="mx-auto px-4">{children}</div>
            </main>

            {/* 侧边栏对话框 */}
            <Dialog open={showSidebar} onOpenChange={setShowSidebar}>
              <CustomDialogContent
                className="fixed inset-x-0 bottom-auto top-2 z-50 h-[calc(98vh-4px)] max-h-[98vh] w-[95%] max-w-[450px] rounded-xl border-2 border-emerald-500/40 bg-black p-0 shadow-[0_0_30px_rgba(16,185,129,0.4)] backdrop-blur-xl"
                style={{ transform: 'translate(-50%, 0)', left: '50%' }}
              >
                <DialogTitle className="sr-only">Menu</DialogTitle>
                <div className="relative">
                  <div className="flex items-center justify-between p-4 relative">
                    {/* 电影感底部边框 */}
                    <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
                    <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-emerald-500/20 via-emerald-400/40 to-emerald-500/20 blur-sm" />
                    <div className="flex items-center">
                      <div className="mr-3 h-10 w-10 overflow-hidden rounded-full bg-gradient-to-r from-green-500 to-cyan-600 shadow-[0_0_15px_rgba(37,99,235,0.4)]">
                        <img
                          src="/logo.png"
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <h2 className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-xl font-bold text-transparent">
                        AnyVideo.ai
                      </h2>
                    </div>
                    <DialogClose asChild>
                      <Button
                        variant="ghost"
                        className="h-10 w-10 rounded-full p-0 text-white transition-colors duration-300 hover:bg-white/10"
                      >
                        <X className="h-6 w-6" />
                        <span className="sr-only">Close</span>
                      </Button>
                    </DialogClose>
                  </div>
                  <ScrollArea className="h-[calc(98vh-64px)] overflow-y-auto">
                    <div className="p-6">
                      <Sidebar />
                    </div>
                  </ScrollArea>
                </div>
              </CustomDialogContent>
            </Dialog>

            {/* 历史记录对话框 */}
            <Dialog open={showHistory} onOpenChange={setShowHistory}>
              <CustomDialogContent
                className="fixed inset-x-0 bottom-auto top-2 z-50 h-[calc(98vh-4px)] max-h-[98vh] w-[95%] max-w-[450px] rounded-xl border-2 border-emerald-500/40 bg-black p-0 shadow-[0_0_30px_rgba(16,185,129,0.4)] backdrop-blur-xl overflow-hidden"
                style={{ transform: 'translate(-50%, 0)', left: '50%' }}
              >
                <DialogTitle className="sr-only">History</DialogTitle>
                <div className="flex h-full flex-col">
                  <div className="flex items-center justify-between p-4 flex-shrink-0 relative">
                    {/* 电影感底部边框 */}
                    <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
                    <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-emerald-500/20 via-emerald-400/40 to-emerald-500/20 blur-sm" />
                    <div className="flex items-center">
                      <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-cyan-500 shadow-[0_0_12px_rgba(37,99,235,0.5)]">
                        <Clock className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-xl font-bold text-white">History</h2>
                    </div>
                    <DialogClose asChild>
                      <Button
                        variant="ghost"
                        className="h-9 w-9 rounded-full p-0 text-white transition-colors duration-300 hover:bg-white/10"
                      >
                        <X className="h-5 w-5" />
                        <span className="sr-only">Close</span>
                      </Button>
                    </DialogClose>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <GenerationHistory />
                  </div>
                </div>
              </CustomDialogContent>
            </Dialog>
          </div>
        )}
      </>
    </SidebarProvider>
  );
}
